import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import Header from '../components/Header';
import AnimatedLine from '../components/AnimatedLine';
import AnimatedVerticalLine from '../components/AnimatedVerticalLine';
import Orb from '../components/Orb';
import TypewriterText from '../components/TypewriterText';
import { ChevronDown, Play } from 'lucide-react';

const AttentionLabsLanding = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [typingComplete, setTypingComplete] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    // Show content after 1 second (background plays first)
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const portfolioClients = [
    "https://framerusercontent.com/images/Bjz8UgHPdIAda9n6I2exkkpFk.png?width=1200&height=155",
    "https://framerusercontent.com/images/9okGgKRQvft6nApRWLVOqnt9YOk.png?width=2400&height=797",
    "https://framerusercontent.com/images/6WTYWH6wX2rnWpBfvYNpuRsbL2c.png?width=442&height=150",
    "https://framerusercontent.com/images/4BTH4HuoCW1z5rGc7nCORRX2KPU.png?width=881&height=323",
    "https://framerusercontent.com/images/P20Csz2Tx8IOEJY5JaJxyr6J0M.png?width=336&height=104",
    "https://framerusercontent.com/images/VPooO8yNuf4jzcTTtIxO1z15G54.png?width=8158&height=1621"
  ];

  const videos = [
    { id: "l2lBQ9WzWuY", thumbnail: "https://i.ytimg.com/vi_webp/l2lBQ9WzWuY/sddefault.webp" },
    { id: "MAgkb27U6i0", thumbnail: "https://i.ytimg.com/vi_webp/MAgkb27U6i0/sddefault.webp" },
    { id: "qsgKmij7guQ", thumbnail: "https://i.ytimg.com/vi_webp/qsgKmij7guQ/sddefault.webp" }
  ];

  const services = [
    {
      title: "The Sprint",
      subtitle: "",
      description: "30 days. 4 long-form videos. 10 short-form assets. A full audit of where you stand and what's possible. Built for skeptics who want proof before commitment.",
      includes: [
        "Strategy workshop",
        "4 long-form videos",
        "10 short-form assets",
        "Full audit and analysis"
      ],
      price: "$3,000",
      priceNote: "one-time",
      cta: "Book a Call"
    },
    {
      title: "The Engine",
      subtitle: "",
      description: "We become your external media team. Full channel management across YouTube, LinkedIn, and X. One monthly interview with you, 30+ assets out the door. Community handled.",
      includes: [
        "Full channel management (YouTube, LinkedIn, X)",
        "Monthly interview",
        "30+ assets per month",
        "Community management"
      ],
      price: "$6,000",
      priceNote: "per month",
      cta: "Book a Call"
    },
    {
      title: "The Build",
      subtitle: "",
      description: "For companies ready to launch a standalone media brand. Full concept, production, distribution, and monetization strategy. Priced on scope with performance alignment.",
      includes: [
        "Concept and casting",
        "Full production",
        "Distribution strategy",
        "Monetization strategy"
      ],
      price: "Custom",
      priceNote: "scope + performance",
      cta: "Book a Call"
    }
  ];

  const whyChooseUs = [
    {
      title: "Speed",
      description: "Measurable traction in 30 days or we double output at no cost."
    },
    {
      title: "Guarantee",
      description: "90 days in, if results don't match our case studies, full refund. No questions."
    },
    {
      title: "Reliability",
      description: "Deliverables ship on time. If we miss, next month is free."
    }
  ];

  const testimonials = [
    {
      quote: "These people are the real commandos of content.",
      name: "Shivender Kanwar",
      title: "Ex Para SF",
      image: "https://framerusercontent.com/images/UWPvLAi5YrL7SazOLSfhyPqXk.jpg?width=6000&height=4000"
    },
    {
      quote: "You are the real MVP.",
      name: "Rahul Seth",
      title: "Director at Antler",
      image: "https://framerusercontent.com/images/spuc2ytXfj48L2KdzHw1irAWa8.jpeg?width=738&height=738"
    },
    {
      quote: "Everyone in the office loved it. Great job!",
      name: "Arushi Nayak",
      title: "Social Media Manager at WTFund",
      image: "https://framerusercontent.com/images/6zcr8WIETfAZyko0I37u4w1kYw.png?width=462&height=494"
    },
    {
      quote: "You guys are the wizards.",
      name: "Ariana Koyel",
      title: "Social Media Influencer",
      image: "https://framerusercontent.com/images/bVjkbOzZTKN6FDBqgIlcuWHjU.png?width=892&height=922"
    }
  ];

  const faqs = [
    {
      question: "What do I have to do?",
      answer: "Show up for one interview per month. We handle everything else."
    },
    {
      question: "What kind of companies do you work with?",
      answer: "Founders, startups, funds (institutional and retail), and businesses building owned media. We go deep on AI, fintech, B2B SaaS, and technical products."
    },
    {
      question: "Do you build podcasts and newsletters?",
      answer: "Yes. Media properties are core to what we do - we build and run podcasts, newsletters, and YouTube channels."
    },
    {
      question: "How fast do you move?",
      answer: "First assets ship in 2 weeks. The Sprint delivers full proof-of-concept in 30 days."
    },
    {
      question: "What if it doesn't work?",
      answer: "90-day guarantee. If results don't match our case studies, full refund."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Fixed Background Animation */}
      <div className="fixed inset-0 z-0">
        <Orb
          hoverIntensity={0}
          rotateOnHover={true}
          hue={0}
          forceHoverState={false}
        />
      </div>

      <div className="relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showContent ? 1 : 0 }}
          transition={{ duration: 1 }}
        >
          <Header />
        </motion.div>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif', lineHeight: '1.2' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: showContent ? 1 : 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            <div>
              <TypewriterText 
                words={['We', 'engineer', 'attention.']} 
                delay={500}
                speed={100}
                wordDelay={200}
                onComplete={() => setTypingComplete(true)}
              />
            </div>
          </motion.div>
          <motion.p 
            className="text-lg sm:text-xl md:text-2xl text-white/70 max-w-4xl mx-auto mb-12" 
            style={{ fontFamily: 'Inter, sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 20 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Most companies rent attention. We build media you own.
          </motion.p>
          <motion.a 
            href="#contact" 
            className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all text-lg"
            style={{ fontFamily: 'Inter, sans-serif' }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: showContent ? 1 : 0, scale: showContent ? 1 : 0.8 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Book a Call
          </motion.a>
          <motion.p 
            className="text-sm text-white/50 mt-4" 
            style={{ fontFamily: 'Inter, sans-serif' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: showContent ? 1 : 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Accepting clients for Q1 2026.
          </motion.p>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ 
            opacity: typingComplete ? 1 : 0, 
            y: typingComplete ? [0, 10, 0] : -20 
          }}
          transition={{ 
            opacity: { duration: 0.5, delay: 0.5 },
            y: { 
              duration: 1.5, 
              repeat: Infinity, 
              ease: "easeInOut",
              delay: 0.5
            }
          }}
        >
          <ChevronDown className="text-white/50" size={24} />
        </motion.div>
      </section>

      <AnimatedLine />

      {/* All sections below hero have black background */}
      <div className="bg-black">
      
      {/* The Thesis Section */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-12 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            The Problem
          </h2>
          
          <div className="space-y-8 text-lg sm:text-xl text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>
            <p>
              You're renting attention from platforms that charge more every year for the same audience. Every dollar spent on ads is gone the moment the campaign ends. That's not an asset. That's a leak.
            </p>
          </div>
          
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-12 mt-16 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            The Fix
          </h2>
          
          <div className="space-y-8 text-lg sm:text-xl text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>
            <p>
              We build owned media infrastructure - content that ranks, gets shared, and generates demand while you sleep. Channels that appreciate. Libraries that compound. Distribution you control.
            </p>
          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* The Who Section */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-16 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Who This Is For
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Founders with no distribution
              </h3>
              <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                You built something real but nobody knows it exists. We turn your expertise into a media engine that fills your pipeline.
              </p>
            </div>

            <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Firms that need trust at scale
              </h3>
              <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                You have capital, credentials, or clients - but no public presence. We build the content arm that makes strangers trust you before the first call.
              </p>
            </div>

            <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Companies done with agency jargon
              </h3>
              <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                You've burned budget on retainers that produced nothing. You want output, velocity, and results you can actually measure.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AnimatedLine />
      
      {/* Portfolio Clients Section */}
      <section className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-sm font-semibold text-white/50 mb-12 tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>
            PORTFOLIO CLIENTS
          </h2>
          
          {/* Infinite scroll animation */}
          <div className="overflow-hidden">
            <div className="flex gap-12 animate-scroll w-max">
              {[...portfolioClients, ...portfolioClients, ...portfolioClients, ...portfolioClients].map((client, idx) => (
                <div key={idx} className="flex-shrink-0">
                  <img src={client} alt="Client logo" className="h-12 w-auto opacity-50 hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* Stats Section */}
      <section className="relative lg:min-h-screen flex flex-col lg:flex-row">
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-16 py-12 sm:py-16 lg:py-24">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-12">
            <div>
              <div className="text-6xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                1000+
              </div>
              <div className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                Videos Created
              </div>
            </div>
            <div>
              <div className="text-6xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                200+
              </div>
              <div className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                Clients Served
              </div>
            </div>
            <div>
              <div className="text-6xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                80M+
              </div>
              <div className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                Views Generated
              </div>
            </div>
          </div>
        </div>

        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0">
          <AnimatedVerticalLine delay={0.5} />
        </div>

        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-hidden">
          <div className="relative w-full max-w-2xl h-[400px] overflow-hidden rounded-lg">
            <div className="animate-scroll-vertical">
              {[...videos, ...videos].map((video, idx) => (
                <div 
                  key={idx}
                  className="relative w-full h-[400px] mb-8"
                >
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?autoplay=1&mute=1&loop=1&playlist=${video.id}&controls=0&modestbranding=1&rel=0`}
                    title="Video player"
                    allow="autoplay; encrypted-media"
                    className="w-full h-full rounded-lg"
                    style={{ border: 'none' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* Services Section */}
      <section id="services" className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            How We Work
          </h2>
          <p className="text-center text-white/70 mb-16 text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
            
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div key={idx} className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors flex flex-col">
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-white/70 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {service.description}
                </p>
                <div className="mb-6">
                  <ul className="space-y-2">
                    {service.includes.map((item, i) => (
                      <li key={i} className="text-white/70 text-sm flex items-start gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        <span className="text-white/50 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto">
                  <div className="mb-4">
                    <div className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                      {service.price}
                    </div>
                    <div className="text-sm text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {service.priceNote}
                    </div>
                  </div>
                  <a 
                    href="#contact"
                    className="block w-full text-center px-6 py-3 border border-white/20 rounded-lg hover:bg-white/10 transition-colors font-semibold"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {service.cta}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* Why Attention Labs Section */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-16 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Why Attention Labs
          </h2>
          
          <div className="space-y-8">
            <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Technical fluency
              </h3>
              <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                We go deep on AI, fintech, infra - not just fluff.
              </p>
            </div>

            <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Capital + consumer native
              </h3>
              <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                We understand VC signaling AND retail trust.
              </p>
            </div>

            <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Founder-operator background
              </h3>
              <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                We've built and scaled companies, not just advised them.
              </p>
            </div>

            <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Output, not overhead
              </h3>
              <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                We run it. You get results.
              </p>
            </div>

            <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Full-stack
              </h3>
              <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                From founder brand to media property to launch.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* Why Owned Media Wins Section */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-12 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Why Owned Media Wins
          </h2>
          
          <div className="space-y-8 text-lg sm:text-xl text-white/80 mb-12" style={{ fontFamily: 'Inter, sans-serif' }}>
            <p>
              Most companies rent attention. Ads, PR, agencies - it disappears when you stop paying.
            </p>
            <p>
              We build infrastructure that compounds:
            </p>
          </div>

          <div className="space-y-4 mb-12 text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
            <div className="flex items-start gap-3">
              <span className="text-white/50 mt-1">•</span>
              <span className="text-white/80">Founder brands that become cults</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-white/50 mt-1">•</span>
              <span className="text-white/80">Content engines that run without you</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-white/50 mt-1">•</span>
              <span className="text-white/80">Media properties you own, not rent</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-white/50 mt-1">•</span>
              <span className="text-white/80">Launches that break through the noise</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-white/50 mt-1">•</span>
              <span className="text-white/80">Distribution that compounds while you sleep</span>
            </div>
          </div>

          <blockquote className="border-l-4 border-purple-500 pl-6 py-4 text-xl italic text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>
            "We don't buy attention. We build machines that generate it."
          </blockquote>
        </div>
      </section>

      <AnimatedLine />

      {/* Why Choose Us Section */}
      <section className="relative lg:min-h-screen flex flex-row">
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-16 py-12 sm:py-16 lg:py-24">
          <h2 className="text-4xl sm:text-5xl font-bold mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            What You Get
          </h2>
          
          <div className="space-y-8">
            {whyChooseUs.map((item, idx) => (
              <div key={idx} className="flex flex-row items-start gap-6">
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {item.description}
                  </p>
                </div>
                
                <div className="lg:hidden flex-shrink-0 flex items-center">
                  <motion.div
                    className="relative"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.5 + idx * 0.3, duration: 0.5 }}
                  >
                    <div className="w-10 h-10 rounded-full border-2 border-purple-500 flex items-center justify-center">
                      <div className="w-5 h-5 rounded-full bg-purple-500"></div>
                    </div>
                  </motion.div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0">
          <AnimatedVerticalLine delay={0.5} />
        </div>

        <div className="hidden lg:flex w-full lg:w-1/2 items-center justify-center p-8">
          {/* <div className="w-full max-w-md aspect-square bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl"></div> */}
          
          {/* Progress Chart */}
          <div className="relative w-full max-w-md h-[400px] flex items-center justify-center">
            <svg className="w-full h-full" viewBox="0 0 200 400">
              {/* Vertical Line */}
              <motion.line
                x1="100"
                y1="50"
                x2="100"
                y2="350"
                stroke="rgba(255, 255, 255, 0.2)"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, ease: "easeInOut" }}
              />
              
              {/* Speed Circle */}
              <motion.circle
                cx="100"
                cy="100"
                r="20"
                fill="none"
                stroke="#8b5cf6"
                strokeWidth="3"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              />
              <motion.circle
                cx="100"
                cy="100"
                r="12"
                fill="#8b5cf6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.7, duration: 0.3 }}
              />
              
              {/* Guarantee Circle */}
              <motion.circle
                cx="100"
                cy="200"
                r="20"
                fill="none"
                stroke="#8b5cf6"
                strokeWidth="3"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
              />
              <motion.circle
                cx="100"
                cy="200"
                r="12"
                fill="#8b5cf6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.2, duration: 0.3 }}
              />
              
              {/* Reliability Circle */}
              <motion.circle
                cx="100"
                cy="300"
                r="20"
                fill="none"
                stroke="#8b5cf6"
                strokeWidth="3"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.5 }}
              />
              <motion.circle
                cx="100"
                cy="300"
                r="12"
                fill="#8b5cf6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.7, duration: 0.3 }}
              />
            </svg>
          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* Testimonials Section */}
      <section className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            What Clients Say
          </h2>
          <p className="text-center text-white/70 mb-16" style={{ fontFamily: 'Inter, sans-serif' }}>
            
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="border border-white/10 rounded-lg p-6 hover:border-white/30 transition-colors">
                <div className="mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                </div>
                <p className="text-lg mb-4 italic" style={{ fontFamily: 'Inter, sans-serif' }}>
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {testimonial.title}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* FAQs Section */}
      <section className="relative py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            FAQs
          </h2>
          <p className="text-center text-white/70 mb-16" style={{ fontFamily: 'Inter, sans-serif' }}>
            Quick answers to your questions.
          </p>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border border-white/10 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`transition-transform ${openFAQ === idx ? 'rotate-180' : ''}`}
                    size={20}
                  />
                </button>
                {openFAQ === idx && (
                  <div className="px-6 py-4 bg-white/5 border-t border-white/10">
                    <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* Final CTA Section */}
      <section id="contact" className="relative py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Ready?
          </h2>
          <p className="text-xl text-white/70 mb-12" style={{ fontFamily: 'Inter, sans-serif' }}>
            Book a call. We'll tell you if we can help - and if we can't, we'll tell you that too.
          </p>
          <a 
            href="https://calendly.com/contact-attentionlabs/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all text-lg"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Book a Call
          </a>
        </div>
      </section>

      <AnimatedLine />

      {/* Footer */}
      <footer className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://framerusercontent.com/images/mCmSpcpcYCZJTSJSoearkxxG0FQ.png?width=236&height=240" 
                  alt="AttentionLabs" 
                  className="h-10 w-10"
                />
                <span className="text-white font-bold text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  AttentionLabs
                </span>
              </div>
              <p className="text-white/70 text-sm mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                We engineer attention.
              </p>
              <p className="text-white/70 text-sm mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                811, Tower C, Unitech Business Zone<br />
                Sector 50, Gurugram, India
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Links</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#services" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Methodology</a></li>
                <li><a href="#portfolio" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Portfolio</a></li>
                <li><a href="/casestudies" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Case Studies</a></li>
                <li><a href="#why-us" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Why Us</a></li>
                <li><a href="#testimonials" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Testimonials</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Pages</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="/" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Home</a></li>
                <li><a href="/portfolio" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Portfolio</a></li>
                <li><a href="/casestudies" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Case Studies</a></li>
                <li><a href="/contact" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Contact</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Socials</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="https://www.instagram.com/attention.labs/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Instagram</a></li>
                <li><a href="https://www.youtube.com/@AttentionLabs-br" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>YouTube</a></li>
                <li><a href="https://www.linkedin.com/company/attentionlabscom/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>LinkedIn</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              Visioned and Crafted by AttentionLabs © All rights reserved
            </p>
          </div>
        </div>
      </footer>
      
      </div>
      {/* End black background wrapper */}
      
      </div>
    </div>
  );
};

export default AttentionLabsLanding;
